/**
 * Lightweight client-side probe for a persisted **registered** Firebase auth session.
 *
 * Firebase's browser persistence writes to IndexedDB (`firebaseLocalStorageDb` →
 * `firebaseLocalStorage`) with keys prefixed `firebase:authUser:`. This reads the store
 * directly without loading the Firebase SDK, so marketing/static pages can cheaply decide
 * whether to offer Go-to-app vs Log in / Sign up.
 *
 * Anonymous (guest) sessions are explicitly ignored — they don't count as "signed in"
 * for routing decisions on marketing pages. Without this filter, visiting any
 * `(app)`-group route initializes an anonymous Firebase user and the marketing
 * home would then auto-redirect every guest to `/app`.
 */
export async function hasFirebaseSession(): Promise<boolean> {
  if (typeof indexedDB === "undefined") return false;

  // Avoid materializing an empty `firebaseLocalStorageDb` just to check — if the browser
  // supports `databases()` we can see whether it exists first.
  if (typeof indexedDB.databases === "function") {
    try {
      const dbs = await indexedDB.databases();
      if (!dbs.some((db) => db.name === "firebaseLocalStorageDb")) return false;
    } catch {
      // fall through to the open() path
    }
  }

  return new Promise<boolean>((resolve) => {
    let settled = false;
    const done = (found: boolean, db?: IDBDatabase) => {
      if (settled) return;
      settled = true;
      try {
        db?.close();
      } catch (e) {
        console.error(e);
      }
      resolve(found);
    };

    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open("firebaseLocalStorageDb");
    } catch {
      resolve(false);
      return;
    }

    request.addEventListener("error", () => done(false));
    request.onupgradeneeded = () => done(false, request.result);
    request.onsuccess = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains("firebaseLocalStorage")) return done(false, db);

      try {
        const store = db.transaction("firebaseLocalStorage", "readonly").objectStore("firebaseLocalStorage");
        const cursorReq = store.openCursor();
        cursorReq.addEventListener("error", () => done(false, db));
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (!cursor) return done(false, db);
          if (typeof cursor.key === "string" && cursor.key.startsWith("firebase:authUser:")) {
            // Firebase stores the user record at `cursor.value.value` (stringified
            // or already parsed depending on browser). Anonymous users have
            // `isAnonymous: true` — treat those as not signed in.
            const raw = (cursor.value as { value?: unknown })?.value;
            let user: unknown = raw;
            if (typeof raw === "string") {
              try {
                user = JSON.parse(raw);
              } catch {
                user = undefined;
              }
            }
            if (typeof user === "object" && user !== null && "isAnonymous" in user && user.isAnonymous === true) {
              cursor.continue();
              return;
            }
            return done(true, db);
          }
          cursor.continue();
        };
      } catch {
        done(false, db);
      }
    };
  });
}
